import { Fragment, useCallback, useRef, useEffect } from "react"
import Map, { Source, Layer } from "react-map-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import MapLegend from "./MapLegend"
import FlashData from "./FlashData"
import { useState } from "react"
import DistanceView from "./DistanceView"
import handleClick from "./handleClick"
import FacilityLegend from "./FacilityLegend"
import RightClickMenu from "./RightClickMenu"
import { MapStyleHook } from "./map_style"
import initial_map_settings from "../../configs/initial_map_settings.json"
import facility_types from "../../configs/facility_types.json"
import break_defs from "../../configs/breaks.json"
import theme_colors from "../../configs/theme_colors.json"

var hash_value = function (s) {
  /* Simple hash function. */
  var a = 1,
    c = 0,
    h,
    o
  if (s) {
    a = 0
    /*jshint plusplus:false bitwise:false*/
    for (h = s.length - 1; h >= 0; h--) {
      o = s.charCodeAt(h)
      a = ((a << 6) & 268435455) + o + (o << 14)
      c = a & 266338304
      a = c !== 0 ? a ^ (c >> 21) : a
    }
  }
  return String(a)
}

var string_to_colour = function (str) {
  var hash = hash_value(str)
  var colour = "#"
  for (var j = 0; j < 3; j++) {
    var value = (hash >> (j * 8)) & 0xff
    colour += ("00" + value.toString(16)).substr(-2)
  }
  return colour
}

const make_catchment_layer = (au_geo_json, visualisation_settings) => {
  const { au_render_prop, show_au_layer, analysis_units_opacity } =
    visualisation_settings

  if (
    !au_geo_json ||
    !show_au_layer ||
    !(
      au_render_prop === "catchment_group" ||
      au_render_prop === "allocated_to_group" ||
      au_render_prop === "cup_id"
    )
  ) {
    return {
      id: "analysis_units_catchment",
      type: "fill",
      layout: { visibility: "none" },
    }
  }

  let all_types = []
  au_geo_json.features.forEach((feature) => {
    const value = feature.properties[au_render_prop]
    if (value) {
      if (!all_types.includes(value)) {
        all_types.push(value)
      }
    }
  })

  let facility_and_colour = []
  all_types.forEach((type) => {
    facility_and_colour.push(type)
    if (type === -1) {
      facility_and_colour.push("#FFFFFF")
    } else if (type === -2) {
      facility_and_colour.push("#000000")
    } else {
      facility_and_colour.push(string_to_colour("facility" + type))
    }
  })

  return {
    id: "analysis_units_catchment",
    type: "fill",
    paint: {
      "fill-color": [
        "match",
        ["get", au_render_prop],
        ...facility_and_colour,
        "white",
      ],
      "fill-opacity": analysis_units_opacity,
    },
  }
}

const make_shaded_au_data_layer = (au_geo_json, visualisation_settings) => {
  const { au_render_prop, show_au_layer, analysis_units_opacity } =
    visualisation_settings

  if (
    !au_geo_json ||
    !show_au_layer ||
    au_render_prop === "catchment_group" ||
    au_render_prop === "allocated_to_group"
  ) {
    return {
      id: "analysis_units_shaded",
      type: "fill",
      layout: { visibility: "none" },
    }
  }

  const break_data =
    au_render_prop === "distance_to_nearest_facility"
      ? break_defs.linear_breaks
      : break_defs.exp_breaks

  const breaks = []
  for (const [key, [, upper]] of Object.entries(break_data)) {
    breaks.push(key)
    if (upper !== Infinity) {
      breaks.push(upper)
    }
  }

  return {
    id: "analysis_units_shaded",
    type: "fill",
    layout: { visibility: "visible" },
    paint: {
      "fill-opacity": analysis_units_opacity,
      "fill-outline-color": au_render_prop
        ? "rgba(255,255,255,0.1})"
        : "rgba(0,0,0,0.3})",
      "fill-color": au_render_prop
        ? ["step", ["get", au_render_prop], ...breaks]
        : "grey",
    },
  }
}

const BoundarySource = ({
  show,
  id,
  colour,
  clickable = false,
  highlight_property = "",
  highlight_id = null,
}) => {
  return (
    <Source data={null} type="geojson" id={`source_${id}`}>
      <Layer
        id={`${id}_boundary`}
        type={"line"}
        layout={{ visibility: show ? "visible" : "none" }}
        paint={{
          "line-color": colour,
          "line-opacity":
            id === "cups_boundary" || "districts_boundary" ? 0.6 : 1,
          "line-width":
            id === "cups_boundary" || "districts_boundary" ? 2.5 : 1,
        }}
      />
      {clickable ? (
        <Layer
          id={id}
          type={"fill"}
          layout={{ visibility: show ? "visible" : "none" }}
          paint={{
            "fill-color": "rgba(255, 255, 255, 0)",
            "fill-outline-color": colour,
            "fill-opacity": 1,
          }}
        />
      ) : null}

      {highlight_property ? (
        // Highlights a feature based on a property and OD
        <Layer
          id={`${id}_highlight`}
          type="fill"
          filter={["==", ["id"], highlight_id]}
          layout={{
            visibility: show && highlight_id !== null ? "visible" : "none",
          }}
          paint={{
            "fill-color": colour,
            "fill-opacity": 0.5,
          }}
        />
      ) : null}
    </Source>
  )
}

const PointSource = ({
  show,
  show_labels,
  id,
  size,
  symbol,
  colour,
  dynamic = false,
}) => {
  return (
    <Source
      data={{ type: "FeatureCollection", features: [] }}
      type="geojson"
      id={`source_${id}`}
      dynamic={dynamic}
    >
      <Layer
        id={id}
        type="symbol"
        layout={{
          "text-field": symbol,
          "text-size": size * 5.1,
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-allow-overlap": true,
          visibility: show ? "visible" : "none",
        }}
        paint={{
          "text-color": colour,
          "text-opacity": 0.9,
        }}
      />
      <Layer
        id={`${id}_labels`}
        type="symbol"
        layout={{
          "text-field": ["get", "name"],
          "text-rotation-alignment": "auto",
          "text-allow-overlap": false,
          "text-anchor": "bottom",
          "text-radial-offset": 0.8,
          visibility: show && show_labels ? "visible" : "none",
        }}
      />
    </Source>
  )
}

const FacilitySource = ({ visualisation_settings }) => {
  const size = visualisation_settings.facility_radius
  const id = "facilities"
  const show = true
  const { show_fac_labels, visible_facilities } = visualisation_settings

  return (
    <Source
      data={{ type: "FeatureCollection", features: [] }}
      type="geojson"
      id={`source_${id}`}
      dynamic={true}
    >
      <Layer
        id={id}
        type="symbol"
        layout={{
          "text-field": [
            "match",
            ["get", "type"],
            ...facility_types.map(({ type, symbol }) => [type, symbol]).flat(),
            /* default */ "•",
          ],
          "text-size": size * 5.1,
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-allow-overlap": true,
          visibility: show ? "visible" : "none",
        }}
        filter={
          visible_facilities.length > 0
            ? ["match", ["get", "type"], visible_facilities, true, false]
            : false
        }
        paint={{
          "text-color": [
            "match",
            ["get", "type"],
            ...facility_types.map(({ type, colour }) => [type, colour]).flat(),
            /* default */ "black",
          ],
          "text-opacity": 0.9,
        }}
      />
      <Layer
        id={`${id}_labels`}
        type="symbol"
        filter={
          visible_facilities.length > 0
            ? ["match", ["get", "type"], visible_facilities, true, false]
            : false
        }
        layout={{
          "text-field": ["get", "name"],
          "text-rotation-alignment": "auto",
          "text-allow-overlap": false,
          "text-anchor": "bottom",
          "text-radial-offset": 0.8,
          visibility: show && show_fac_labels ? "visible" : "none",
        }}
      />
    </Source>
  )
}

export default function MapView({
  map_data,
  dispatch_map_data,
  dispatch_ui,
  map_ref,
  set_map_ref,
  visualisation_settings,
  current_view_cup_id,
  current_view_district_id,
  set_change_requests,
}) {
  const [menu_position, set_menu_position] = useState(null)
  const [clicked_feature, set_clicked_feature] = useState(null)
  const [measure_mode, set_measure_mode] = useState(false)
  const [map_style, set_map_style] = useState(false)
  const [distance_query, set_distance_query] = useState({
    last_updated: null,
    from: null,
    to: null,
    distance: null,
  })

  const { fac_geo_json, au_geo_json, cup_geo_json, cup_boundary_geo_json } =
    map_data

  FlashData(map_ref, "source_facilities", fac_geo_json)
  FlashData(map_ref, "source_analysis_units", au_geo_json)
  FlashData(map_ref, "source_cups", cup_geo_json)
  FlashData(map_ref, "source_cups_boundary", cup_boundary_geo_json)

  MapStyleHook(set_map_style)

  const { center, zoom } = initial_map_settings

  const menu_ref = useRef(null)

  useEffect(() => {
    const handle_click_outside = (event) => {
      if (menu_ref.current && !menu_ref.current.contains(event.target)) {
        set_menu_position(null)
      }
    }
    document.addEventListener("mousedown", handle_click_outside)
    return () => {
      document.removeEventListener("mousedown", handle_click_outside)
    }
  }, [])

  const handle_context_menu = useCallback(
    (event) => {
      event.preventDefault()
      if (!map_ref) return

      set_menu_position({
        x: event.point.x,
        y: event.point.y,
        lngLat: event.lngLat,
      })

      const map = map_ref.getMap()

      if (map.getLayer("facilities")) {
        const features = map.queryRenderedFeatures(event.point, {
          layers: ["facilities"],
        })
        if (features.length > 0) {
          set_clicked_feature(features[0])
          return
        }
      }
      if (map.getLayer("cups")) {
        const features = map.queryRenderedFeatures(event.point, {
          layers: ["cups"],
        })
        if (features.length > 0) {
          set_clicked_feature(features[0])
          return
        }
      }
      if (map.getLayer("district_municipalities")) {
        const features = map.queryRenderedFeatures(event.point, {
          layers: ["district_municipalities"],
        })
        if (features.length > 0) {
          set_clicked_feature(features[0])
          return
        }
      }
      if (map.getLayer("analysis_units_catchment")) {
        const features = map.queryRenderedFeatures(event.point, {
          layers: ["analysis_units_catchment"],
        })
        if (features.length > 0) {
          set_clicked_feature(features[0])
          return
        }
      }
      if (map.getLayer("analysis_units_shaded")) {
        const features = map.queryRenderedFeatures(event.point, {
          layers: ["analysis_units_shaded"],
        })
        if (features.length > 0) {
          set_clicked_feature(features[0])
          return
        }
      }
      set_clicked_feature(null)
    },
    [map_ref],
  )

  const handle_close_menu = () => set_menu_position(null)

  if (!map_style) {
    return null
  }

  return (
    <Fragment>
      <Map
        initialViewState={{
          longitude: center[1],
          latitude: center[0],
          zoom: zoom,
          pitch: 0,
        }}
        style={{ width: "100vw", height: "calc(100vh - 64px)" }}
        mapStyle={map_style}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        ref={(ref) => set_map_ref(ref)}
        interactiveLayerIds={[
          "facilities",
          "cups",
          "district_municipalities",
          "analysis_units_shaded",
        ]}
        onClick={(click) => {
          handleClick({
            click,
            measure_mode,
            distance_query,
            set_distance_query,
          })
        }}
        onContextMenu={handle_context_menu}
      >
        <Source data={null} type="geojson" id="source_analysis_units">
          <Layer
            {...make_shaded_au_data_layer(au_geo_json, visualisation_settings)}
          />
          <Layer
            {...make_catchment_layer(au_geo_json, visualisation_settings)}
          />
        </Source>

        <BoundarySource
          show={visualisation_settings.show_cups}
          id="cups_boundary"
          colour={theme_colors.cups}
          highlight_id={current_view_cup_id}
          highlight_property="cup_id"
        />

        <BoundarySource
          show={visualisation_settings.show_districts}
          id="district_municipalities"
          colour={theme_colors.ndoh_green}
          clickable={true}
          highlight_id={current_view_district_id}
          highlight_property="name"
        />

        <BoundarySource
          show={visualisation_settings.show_sub_districts}
          id="sub_districts"
          colour={theme_colors.ndoh_green}
        />

        <BoundarySource
          show={visualisation_settings.show_provinces}
          id="provinces"
          colour={theme_colors.ndoh_green}
        />
        <BoundarySource
          show={visualisation_settings.show_wards}
          id="wards"
          colour={theme_colors.ndoh_green}
        />

        <FacilitySource visualisation_settings={visualisation_settings} />

        <PointSource
          show={visualisation_settings.show_cups}
          show_labels={visualisation_settings.show_fac_labels}
          id="cups"
          size={visualisation_settings.facility_radius * 0.9}
          symbol={"■"}
          colour={theme_colors.cups}
          dynamic={true}
        />
      </Map>
      {menu_position && (
        <RightClickMenu
          map_data={map_data}
          dispatch_ui={dispatch_ui}
          dispatch_map_data={dispatch_map_data}
          menu_position={menu_position}
          menu_ref={menu_ref}
          clicked_feature={clicked_feature}
          handle_close_menu={handle_close_menu}
          map_ref={map_ref}
          set_distance_query={set_distance_query}
          set_measure_mode={set_measure_mode}
          set_change_requests={set_change_requests}
        />
      )}
      <MapLegend
        au_geo_json={au_geo_json}
        visualisation_settings={visualisation_settings}
      />
      <DistanceView
        measure_mode={measure_mode}
        set_measure_mode={set_measure_mode}
        distance_query={distance_query}
        set_distance_query={set_distance_query}
      />
      <FacilityLegend
        visible_facilities={visualisation_settings.visible_facilities}
      />
    </Fragment>
  )
}
